// Pure SVG edge-path math, ported verbatim from edge_controller.js (itself an
// exact React Flow port — xyflow bezier-edge.ts, smoothstep-edge.ts,
// straight-edge.ts). The module exists so TWO consumers share one copy of the
// math: edge_controller.js (rendered edges) and connection_line_controller.js
// (the drag-time connection preview) — the preview must draw the same shape
// family the edges draw for the canvas's `data-connection-line-type`
// (bezier / smoothstep / step / straight), not a private bezier.
//
// Every function is side-effect free and returns `[path, labelX, labelY]`,
// the shape the controllers' own helpers have always used. Gem JS must not
// import host aliases (no-host-alias-imports.test.ts), so controllers import
// this module by relative path.

// React Flow: calculateControlOffset
export function calculateControlOffset(distance, curvature) {
  if (distance >= 0) return 0.5 * distance
  return curvature * 25 * Math.sqrt(-distance)
}

// React Flow: getControlWithCurvature
export function getControlWithCurvature(pos, x1, y1, x2, y2, c) {
  switch (pos) {
    case "left":   return [x1 - calculateControlOffset(x1 - x2, c), y1]
    case "right":  return [x1 + calculateControlOffset(x2 - x1, c), y1]
    case "top":    return [x1, y1 - calculateControlOffset(y1 - y2, c)]
    case "bottom": return [x1, y1 + calculateControlOffset(y2 - y1, c)]
    default:       return [x1 + calculateControlOffset(x2 - x1, c), y1]
  }
}

// React Flow: getBezierEdgeCenter — cubic bezier t=0.5 midpoint
export function getBezierEdgeCenter(sourceX, sourceY, targetX, targetY, scx, scy, tcx, tcy) {
  const centerX = sourceX * 0.125 + scx * 0.375 + tcx * 0.375 + targetX * 0.125
  const centerY = sourceY * 0.125 + scy * 0.375 + tcy * 0.375 + targetY * 0.125
  return [centerX, centerY]
}

// React Flow: getEdgeCenter
export function getEdgeCenter(sourceX, sourceY, targetX, targetY) {
  const xOffset = Math.abs(targetX - sourceX) / 2
  const centerX = targetX < sourceX ? targetX + xOffset : targetX - xOffset
  const yOffset = Math.abs(targetY - sourceY) / 2
  const centerY = targetY < sourceY ? targetY + yOffset : targetY - yOffset
  return [centerX, centerY]
}

// React Flow: getPoints — orthogonal edge routing
export function getStepPoints(source, sourcePosition, target, targetPosition, offset) {
  const handleDirections = {
    left:   { x: -1, y: 0 },
    right:  { x: 1, y: 0 },
    top:    { x: 0, y: -1 },
    bottom: { x: 0, y: 1 }
  }

  const sourceDir = handleDirections[sourcePosition] || handleDirections.right
  const targetDir = handleDirections[targetPosition] || handleDirections.left

  const sourceGapped = { x: source.x + sourceDir.x * offset, y: source.y + sourceDir.y * offset }
  const targetGapped = { x: target.x + targetDir.x * offset, y: target.y + targetDir.y * offset }

  // Direction from source to target
  const dir = getDirection(sourceGapped, sourcePosition, targetGapped)
  const dirAccessor = dir.x !== 0 ? "x" : "y"
  const currDir = dir[dirAccessor]

  let points = []
  let centerX, centerY
  const sourceGapOffset = { x: 0, y: 0 }
  const targetGapOffset = { x: 0, y: 0 }

  const [defaultCenterX, defaultCenterY] = getEdgeCenter(source.x, source.y, target.x, target.y)

  // Opposite handle positions (e.g., right→left, bottom→top)
  if (sourceDir[dirAccessor] * targetDir[dirAccessor] === -1) {
    centerX = (sourceGapped.x + targetGapped.x) / 2
    centerY = (sourceGapped.y + targetGapped.y) / 2

    const verticalSplit = [
      { x: centerX, y: sourceGapped.y },
      { x: centerX, y: targetGapped.y }
    ]
    const horizontalSplit = [
      { x: sourceGapped.x, y: centerY },
      { x: targetGapped.x, y: centerY }
    ]

    if (sourceDir[dirAccessor] === currDir) {
      points = dirAccessor === "x" ? verticalSplit : horizontalSplit
    } else {
      points = dirAccessor === "x" ? horizontalSplit : verticalSplit
    }
  } else {
    // Same or perpendicular directions
    const sourceTarget = [{ x: sourceGapped.x, y: targetGapped.y }]
    const targetSource = [{ x: targetGapped.x, y: sourceGapped.y }]

    if (dirAccessor === "x") {
      points = sourceDir.x === currDir ? targetSource : sourceTarget
    } else {
      points = sourceDir.y === currDir ? sourceTarget : targetSource
    }

    // Handle same positions edge case
    if (sourcePosition === targetPosition) {
      const diff = Math.abs(source[dirAccessor] - target[dirAccessor])
      if (diff <= offset) {
        const gapOffset = Math.min(offset - 1, offset - diff)
        if (sourceDir[dirAccessor] === currDir) {
          sourceGapOffset[dirAccessor] = (sourceGapped[dirAccessor] > source[dirAccessor] ? -1 : 1) * gapOffset
        } else {
          targetGapOffset[dirAccessor] = (targetGapped[dirAccessor] > target[dirAccessor] ? -1 : 1) * gapOffset
        }
      }
    }

    // Mixed handle positions (e.g., right→bottom)
    if (sourcePosition !== targetPosition) {
      const dirAccessorOpposite = dirAccessor === "x" ? "y" : "x"
      const isSameDir = sourceDir[dirAccessor] === targetDir[dirAccessorOpposite]
      const sourceGtTargetOppo = sourceGapped[dirAccessorOpposite] > targetGapped[dirAccessorOpposite]
      const sourceLtTargetOppo = sourceGapped[dirAccessorOpposite] < targetGapped[dirAccessorOpposite]
      const flipSourceTarget =
        (sourceDir[dirAccessor] === 1 && ((!isSameDir && sourceGtTargetOppo) || (isSameDir && sourceLtTargetOppo))) ||
        (sourceDir[dirAccessor] !== 1 && ((!isSameDir && sourceLtTargetOppo) || (isSameDir && sourceGtTargetOppo)))
      if (flipSourceTarget) {
        points = dirAccessor === "x" ? sourceTarget : targetSource
      }
    }

    const sourceGapPoint = { x: sourceGapped.x + sourceGapOffset.x, y: sourceGapped.y + sourceGapOffset.y }
    const targetGapPoint = { x: targetGapped.x + targetGapOffset.x, y: targetGapped.y + targetGapOffset.y }
    const maxXDistance = Math.max(Math.abs(sourceGapPoint.x - points[0].x), Math.abs(targetGapPoint.x - points[0].x))
    const maxYDistance = Math.max(Math.abs(sourceGapPoint.y - points[0].y), Math.abs(targetGapPoint.y - points[0].y))

    if (maxXDistance >= maxYDistance) {
      centerX = (sourceGapPoint.x + targetGapPoint.x) / 2
      centerY = points[0].y
    } else {
      centerX = points[0].x
      centerY = (sourceGapPoint.y + targetGapPoint.y) / 2
    }
  }

  const gappedSource = { x: sourceGapped.x + sourceGapOffset.x, y: sourceGapped.y + sourceGapOffset.y }
  const gappedTarget = { x: targetGapped.x + targetGapOffset.x, y: targetGapped.y + targetGapOffset.y }

  const pathPoints = [
    source,
    ...(gappedSource.x !== points[0].x || gappedSource.y !== points[0].y ? [gappedSource] : []),
    ...points,
    ...(gappedTarget.x !== points[points.length - 1].x || gappedTarget.y !== points[points.length - 1].y ? [gappedTarget] : []),
    target
  ]

  return [pathPoints, centerX || defaultCenterX, centerY || defaultCenterY]
}

export function getDirection(source, sourcePosition, target) {
  if (sourcePosition === "left" || sourcePosition === "right") {
    return source.x < target.x ? { x: 1, y: 0 } : { x: -1, y: 0 }
  }
  return source.y < target.y ? { x: 0, y: 1 } : { x: 0, y: -1 }
}

// React Flow: getBend — rounded corner for smoothstep
export function getBend(a, b, c, size) {
  const dist = (p1, p2) => Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2))
  const bendSize = Math.min(dist(a, b) / 2, dist(b, c) / 2, size)
  const { x, y } = b

  // No bend needed (collinear)
  if ((a.x === x && x === c.x) || (a.y === y && y === c.y)) {
    return `L${x} ${y}`
  }

  // First segment is horizontal
  if (a.y === y) {
    const xDir = a.x < c.x ? -1 : 1
    const yDir = a.y < c.y ? 1 : -1
    return `L ${x + bendSize * xDir},${y}Q ${x},${y} ${x},${y + bendSize * yDir}`
  }

  // First segment is vertical
  const xDir = a.x < c.x ? 1 : -1
  const yDir = a.y < c.y ? -1 : 1
  return `L ${x},${y + bendSize * yDir}Q ${x},${y} ${x + bendSize * xDir},${y}`
}

// React Flow: getBezierPath — returns [path, labelX, labelY]
export function getBezierPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature) {
  const [sourceControlX, sourceControlY] = getControlWithCurvature(
    sourcePosition, sourceX, sourceY, targetX, targetY, curvature
  )
  const [targetControlX, targetControlY] = getControlWithCurvature(
    targetPosition, targetX, targetY, sourceX, sourceY, curvature
  )
  const [labelX, labelY] = getBezierEdgeCenter(
    sourceX, sourceY, targetX, targetY,
    sourceControlX, sourceControlY, targetControlX, targetControlY
  )
  const path = `M${sourceX},${sourceY} C${sourceControlX},${sourceControlY} ${targetControlX},${targetControlY} ${targetX},${targetY}`
  return [path, labelX, labelY]
}

// React Flow: getSmoothStepPath — returns [path, labelX, labelY];
// borderRadius 0 degenerates to the sharp `step` variant.
export function getSmoothStepPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, borderRadius, offset) {
  const [points, labelX, labelY] = getStepPoints(
    { x: sourceX, y: sourceY }, sourcePosition,
    { x: targetX, y: targetY }, targetPosition,
    offset
  )

  let path = `M${points[0].x} ${points[0].y}`

  for (let i = 1; i < points.length - 1; i++) {
    path += getBend(points[i - 1], points[i], points[i + 1], borderRadius)
  }

  path += `L${points[points.length - 1].x} ${points[points.length - 1].y}`

  return [path, labelX, labelY]
}

// React Flow: getStraightEdgePath — returns [path, labelX, labelY]
export function getStraightPath(sourceX, sourceY, targetX, targetY) {
  const [labelX, labelY] = getEdgeCenter(sourceX, sourceY, targetX, targetY)
  return [`M${sourceX},${sourceY} L${targetX},${targetY}`, labelX, labelY]
}
